import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { PITCH_DECK_STATIC_OPTIONS_QUERY } from '@src/components/sco/PitchDeckAuditor/gql/operations';
import { PitchDeckStaticLookups } from '@src/components/sco/PitchDeckAuditor/model/adapt';
import { useMemo } from 'react';
type StaticOption = {
  id?: string;
  key?: string | null;
  title?: string | null;
  description?: string | null;
  colorHexCode?: string | null;
  type?: string | null;
  strength?: number | null;
};
export type UsePitchDeckStaticOptionsValue = {
  lookups: PitchDeckStaticLookups;
  ratings: StaticOption[];
  slideTypes: StaticOption[];
  rules: StaticOption[];
  coherenceTypes: StaticOption[];
  loading: boolean;
};
const byStrength = (a: StaticOption, b: StaticOption) => (a.strength ?? 0) - (b.strength ?? 0);

/**
 * Loads the pitch-deck reference config (ratings / slide types / rubric rules / coherence
 * dimensions) and derives lookup maps for the adapter. Cached — these change rarely.
 */
export function usePitchDeckStaticOptions() {
  const $ = _c(17);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      fetchPolicy: "cache-first"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const query = useQuery(PITCH_DECK_STATIC_OPTIONS_QUERY, t0);
  const options = (query.data as any)?.scoStaticOptions;
  let t1;
  if ($[1] !== options?.pitchDeckRatings) {
    t1 = options?.pitchDeckRatings ?? [];
    $[1] = options?.pitchDeckRatings;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const ratings = t1;
  let coherenceTypes;
  let rules;
  let slideTypes;
  let t2;
  if ($[3] !== options?.pitchDeckCoherenceTypes || $[4] !== options?.pitchDeckRules || $[5] !== options?.pitchDeckSlideTypes) {
    slideTypes = options?.pitchDeckSlideTypes ?? [];
    rules = options?.pitchDeckRules ?? [];
    coherenceTypes = options?.pitchDeckCoherenceTypes ?? [];
    const ruleTitleById = {};
    rules.forEach(rule => {
      if (rule.key && rule.title) {
        ruleTitleById[rule.key] = rule.title;
      }
    });
    const slideTypeLabelByKey = {};
    slideTypes.forEach(type => {
      if (type.key && type.title) {
        slideTypeLabelByKey[type.key] = type.title;
      }
    });
    const coherenceLabelByKey = {};
    const coherenceOrder = [];
    coherenceTypes.slice().sort(byStrength).forEach(type_0 => {
      if (type_0.key) {
        coherenceOrder.push(type_0.key);
        if (type_0.title) {
          coherenceLabelByKey[type_0.key] = type_0.title;
        }
      }
    });
    t2 = {
      ruleTitleById,
      slideTypeLabelByKey,
      coherenceLabelByKey,
      coherenceOrder
    };
    $[3] = options?.pitchDeckCoherenceTypes;
    $[4] = options?.pitchDeckRules;
    $[5] = options?.pitchDeckSlideTypes;
    $[6] = coherenceTypes;
    $[7] = rules;
    $[8] = slideTypes;
    $[9] = t2;
  } else {
    coherenceTypes = $[6];
    rules = $[7];
    slideTypes = $[8];
    t2 = $[9];
  }
  let t3;
  if ($[10] !== coherenceTypes || $[11] !== query.loading || $[12] !== ratings || $[13] !== rules || $[14] !== slideTypes || $[15] !== t2) {
    t3 = {
      lookups: t2,
      ratings,
      slideTypes,
      rules,
      coherenceTypes,
      loading: query.loading
    };
    $[10] = coherenceTypes;
    $[11] = query.loading;
    $[12] = ratings;
    $[13] = rules;
    $[14] = slideTypes;
    $[15] = t2;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  return t3;
}